import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useContext,
  useMemo,
} from 'react'

import {
  type ClientSessionCustomerData,
  type UserOrderResponseV2,
  type UserOrderResponseWithFlags,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { type PersonalAdviceProduct } from '../../../personal-advice'
import {
  type SubOrderProductListProps,
  useDirectContactFormOrderSelection,
} from '../../hooks/useDirectContactFormOrderSelection'

type DirectFormDialogContextValue = {
  disableSubject?: boolean
  formSubject?: string
  orderSelectionDialogTitle: string
  prefilledSubOrder?: UserSubOrderV2
  product?: PersonalAdviceProduct
  productList: SubOrderProductListProps[]
  selectedOrder: (UserOrderResponseWithFlags | UserOrderResponseV2) | null
  setOrderSelectionDialogTitle: Dispatch<SetStateAction<string>>
  setProductList: Dispatch<SetStateAction<SubOrderProductListProps[]>>
  setSelectedOrder: Dispatch<
    SetStateAction<(UserOrderResponseWithFlags | UserOrderResponseV2) | null>
  >
  setSelectedSubOrder: Dispatch<SetStateAction<UserSubOrderV2 | null>>
  setSubject: Dispatch<SetStateAction<string>>
  setSubOrderNumber: Dispatch<SetStateAction<string>>
  setUserData: Dispatch<SetStateAction<ClientSessionCustomerData>>
  subject: string
  subOrderNumber: string
  userData: ClientSessionCustomerData
}
const DirectFormDialogContext =
  createContext<DirectFormDialogContextValue | null>(null)

export const useDirectFormDialogContext = (): DirectFormDialogContextValue => {
  const context = useContext(DirectFormDialogContext)

  if (context === null) {
    throw new Error('Direct Contact Form Dialog Not Available')
  }

  return context
}

export const DirectFormDialogContextProvider: FC<{
  children: ReactNode
  disableSubject?: boolean
  formSubject?: string
  prefilledSubOrder?: UserSubOrderV2
  product?: PersonalAdviceProduct
}> = ({
  children,
  disableSubject,
  formSubject,
  prefilledSubOrder,
  product,
}) => {
  const directFormOrderValue =
    useDirectContactFormOrderSelection(prefilledSubOrder)
  const DirectFormDialogContextValue = useMemo(
    () => ({
      ...directFormOrderValue,
      disableSubject,
      formSubject,
      prefilledSubOrder,
      product,
    }),
    [
      directFormOrderValue,
      formSubject,
      product,
      disableSubject,
      prefilledSubOrder,
    ],
  )

  return (
    <DirectFormDialogContext.Provider value={DirectFormDialogContextValue}>
      {children}
    </DirectFormDialogContext.Provider>
  )
}
